import { fieldTypes } from "@/components/operationalSecurityTemplateBuilder/config.js";
import store from "@/store";

/*
  Normilizes a template "flat" to a schema  Vue Formulate with:
  -- Sections and subsections (detected by headers or by f.section/f.subsection)
  -- Visibility with conditional show_if (same section and previous questions)
  -- Conditional comments by section (select and checkbox)
  -- File Sections (file_section)
  -- Comments of review by question (just visible in in_review/finals; editable just by revisor in in_review)
*/
export default class FormNormalizer {
  constructor(vm, templateFields, options = {}) {
    this.vm = vm;
    this.templateFields = templateFields || [];
    this.options = options;
    this.customComponents = options.customComponents || {};
    this.model = options.model || {};
    this.currentState = options.currentState || "in_progress";
    this.isReviewer = !!options.isReviewer;
    this.onUpdateFiles = options.onUpdateFiles || null;
    this.onUpdateComments = options.onUpdateComments || null;
    this.extraData = options.extraData || null;
    this.modelCopy = null;
    this.isFirstLoading = true;
    this.lastRegionOfFacilitySelected = '';
    this.lastFacilitySelected = '';
    this.template = options.template ?? {};
    this.isValidationRequired = !this.template.allow_draft;
    this.formId = options.formId;
    this.facilityId = options.facilityId ?? null;
    this.deferredCommands = [];
  }

  // ---------------- UI helpers ----------------
  convertFieldType(type) {
    const fieldType = fieldTypes.find((fType) => fType.type === type);
    return fieldType ? fieldType.data_type : "text";
  }
  buildGroup() {
    return { component: "div", class: "mt-3", children: [] };
  }
  buildButton(opts = {}) {
    return {
      id: opts.id,
      component: "button",
      name: opts.name,
      type: opts.type,
      disabled: opts.disabled,
      children: [{ component: "span", children: opts.text }],
      class: Array.isArray(opts.classes)
        ? opts.classes.join(" ")
        : [opts.classes].join(" "),
    };
  }

  // --- State Helpers ---
  isFinalState() {
    // finals JUST TO TOTAL BLOCK and HIDE BUTTON
    return ["review", "completed", "readonly"].includes(this.currentState);
  }
  isFormLocked() {
    //In revision blocks inputs; in finals too. (schedule/canceled does not block)
    return ( ["review", "completed", "readonly"].includes(this.currentState) || this.options.disabled === true);
  }

  // --------------Field preprocess --------------
  /**
   * Supports:
   *  1) Fields with f.section / f.subsection
   *  2) Headers:
   *      - type: 'section_header' (level 1 by default; options.level === 2 => subsection)
   *      - type: 'subsection_header'
   */
  getProcessedFields() {
    const out = [];
    const secLabelNone = this.vm.$t("risk.template.no_section") || "Sin sección";

    let currentSection = null;
    let currentSubsection = null;

    const list = Array.isArray(this.templateFields) ? this.templateFields : [];

    for (const raw of list) {
      const f = { ...raw };

      // if has specific sections, respects and resets cursors
      if (typeof f.section !== "undefined") {
        currentSection = f.section || secLabelNone;
        if (typeof f.subsection !== "undefined") currentSubsection = f.subsection || null;
        if (f.type === "section_header" || f.type === "subsection_header") continue;
      }

      // Headers
      if (f.type === "section_header") {
        const lvl = Number((f.options && f.options.level) || 1);
        if (lvl === 1) {
          currentSection = f.name || secLabelNone;
          currentSubsection = null;
        } else {
          currentSubsection = f.name || null;
        }
        continue;
      }
      if (f.type === "subsection_header") {
        currentSubsection = f.name || null;
        continue;
      }

      //normalizes names of section/subsection
      if (!f.section) f.section = currentSection || secLabelNone;
      if (typeof f.subsection === "undefined") f.subsection = currentSubsection || null;

      out.push(f);
    }

    return out;
  }

  // -------------- Groupers --------------
  groupBySection(fields) {
    const secLabelNone = this.vm.$t("risk.template.no_section") || "Sin sección";
    const groups = new Map();
    const order = [];
    (fields || []).forEach((f) => {
      const key = f.section || secLabelNone;
      if (!groups.has(key)) {
        groups.set(key, { fields: [] });
        order.push(key);
      }
      groups.get(key).fields.push({ ...f });
    });
    return { order, groups, secLabelNone };
  }

  groupBySectionSubsection(fields) {
    const { order, groups } = this.groupBySection(fields);
    const structured = new Map(); // secName -> { root:[], sub: Map(name->[]), subOrder:[] }

    order.forEach((secName) => {
      const { fields: secFields } = groups.get(secName) || { fields: [] };
      const entry = { root: [], sub: new Map(), subOrder: [] };

      secFields.forEach((f) => {
        const sub = f.subsection || null;
        if (!sub) {
          entry.root.push(f);
          return;
        }
        if (!entry.sub.has(sub)) {
          entry.sub.set(sub, []);
          entry.subOrder.push(sub);
        }
        entry.sub.get(sub).push(f);
      });

      structured.set(secName, entry);
    });

    return { order, structured, groups };
  }

  // -------------- Visibility (same section top-level) --------------
  isFieldVisible(field, sectionFields, indexInSection, answers) {
    const conds = Array.isArray(field.show_if) ? field.show_if : [];
    const logic = field.show_logic === "any" ? "any" : "all";
    if (!conds.length) return true;

    const idToIndex = new Map((sectionFields || []).map((f, idx) => [f.item_id, idx]));
    const getAnsByItemId = (itemId) => {
      const idx = idToIndex.has(itemId) ? idToIndex.get(itemId) : -1;
      if (idx === -1) return { exists: false, value: undefined, idx: -1, field: null };
      const ref = sectionFields[idx];
      return {
        exists: true,
        idx,
        field: ref,
        value: answers ? answers[ref.name] : undefined,
      };
    };

    const asArray = (v) =>
      Array.isArray(v) ? v : v === undefined || v === null || v === "" ? [] : [v];
    const same = (a, b) => {
      if (Array.isArray(a) || Array.isArray(b)) {
        const aa = asArray(a).map((x) => JSON.stringify(x));
        const bb = asArray(b).map((x) => JSON.stringify(x));
        if (aa.length !== bb.length) return false;
        const setB = new Set(bb);
        return aa.every((x) => setB.has(x));
      }
      return String(a) === String(b);
    };
    const num = (v) =>
      v === "" || v === null || typeof v === "undefined" ? NaN : Number(v);
    const contains = (arr, v) =>
      (arr || []).map((x) => JSON.stringify(x)).includes(JSON.stringify(v));

    const evalOne = (c) => {
      if (!c || !c.field) return false;
      const ref = getAnsByItemId(c.field);
      if (!ref.exists) return false;
      if (ref.idx >= indexInSection) return false;

      const ans = ref.value;
      switch (c.op) {
        case "truthy":
          return !!ans;
        case "falsy":
          return !ans;
        case "==":
          return same(ans, c.value);
        case "!=":
          return !same(ans, c.value);
        case ">":
          return num(ans) > num(c.value);
        case ">=":
          return num(ans) >= num(c.value);
        case "<":
          return num(ans) < num(c.value);
        case "<=":
          return num(ans) <= num(c.value);
        case "in": {
          const arr = asArray(ans)
          let vals = asArray(c.value);
          if(vals.length === 1 && ref.field.type === "checkbox") vals = [true];
          return arr.some((v) => contains(vals, v));
        }
        case "includes_all": {
          const arr = asArray(ans),
            vals = asArray(c.value);
          return vals.every((v) => contains(arr, v));
        }
        case "not_in": {
          const arr = asArray(ans),
            vals = asArray(c.value);
          return vals.every((v) => !contains(arr, v));
        }
        default:
          return false;
      }
    };

    const res = conds.map(evalOne);
    return logic === "any" ? res.some(Boolean) : res.every(Boolean);
  }
  isValueEmpty(value) {
    if (value === null || typeof value === "undefined") return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  }
  isFieldAnswered(field, answers) {
    const value = answers ? answers[field.name] : undefined;

    if (["folio", "placeholder", "commands", "files_section"].includes(field.type)) return true;
    if (field.type === "text" && field.options && field.options.isGroup) {
      return (
        Array.isArray(value) &&
        value.some((entry) => entry && !this.isValueEmpty(entry[field.name]))
      );
    }

    const checkboxOptions =
      field.type === "checkbox" &&
      field.options &&
      field.options.fieldTypeCfg &&
      Array.isArray(field.options.fieldTypeCfg.data)
        ? field.options.fieldTypeCfg.data
        : null;
    if (field.type === "checkbox" && (!checkboxOptions || checkboxOptions.length <= 1)) {
      return (
        typeof value === "boolean" ||
        value === "1" ||
        value === "0" ||
        value === 1 ||
        value === 0
      );
    }

    return !this.isValueEmpty(value);
  }

  computeAllRequiredAnswered(answers = {}) {
    const fields = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });
    const { order, groups } = this.groupBySection(fields);

    for (const secName of order) {
      const secFields = (groups.get(secName) || {}).fields || [];
      for (let idx = 0; idx < secFields.length; idx++) {
        const field = secFields[idx];
        if (!field.options || !field.options.required) continue;
        if (!this.isFieldVisible(field, secFields, idx, answers)) continue;
        if (!this.isFieldAnswered(field, answers)) return false;
      }
    }

    return true;
  }

  // -------------- Utilities files/cleaning --------------
  visibleFileLabels(answersObj = {}) {
    const labels = new Set();
    const fields = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });

    const { order, groups } = this.groupBySection(fields);
    order.forEach((secName) => {
      const sec = groups.get(secName);
      (sec.fields || []).forEach((f, idx) => {
        if (
          f.type === "files_section" &&
          this.isFieldVisible(f, sec.fields, idx, answersObj)
        ) {
          labels.add(f.name);
        }
        if (
          f.type === "commands" &&
          this.isFieldVisible(f, sec.fields, idx, answersObj)
        ) {
          labels.add(f.name);
        }
      });
    });

    return labels;
  }

  pruneModelInvisible(model = {}) {
    const pruned = { ...(model || {}) };
    const fields = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });

    const { order, groups } = this.groupBySection(fields);

    order.forEach((secName) => {
      const sec = groups.get(secName);
      const secFields = sec.fields || [];

      secFields.forEach((f, idx) => {
        const visible = this.isFieldVisible(f, secFields, idx, pruned);
        if (!visible) {
          if (pruned.hasOwnProperty(f.name)) delete pruned[f.name];

          const opts = ((f.options || {}).fieldTypeCfg || {}).data || [];
          if (f.type === "select") {
            const cKey = `${f.name} - comment`;
            if (pruned.hasOwnProperty(cKey)) delete pruned[cKey];
          } else if (f.type === "checkbox") {
            opts.forEach((o) => {
              const cKey = `${f.name} - ${o.name} - comment`;
              if (pruned.hasOwnProperty(cKey)) delete pruned[cKey];
            });
          }

          if (f.type === "files_section" && pruned.hasOwnProperty(f.name)) {
            delete pruned[f.name];
          }
          if (f.type === "commands" && pruned.hasOwnProperty(f.name)) {
            delete pruned[f.name];
          }
          return;
        }

        //clean conditional comments
        const opts = ((f.options || {}).fieldTypeCfg || {}).data || [];
        if (f.type === "select") {
          const sel = pruned[f.name];
          const opt = opts.find((o) => o && o.name === sel);
          const needComment = !!(opt && opt.allowComment);
          const cKey = `${f.name} - comment`;
          if (!needComment && pruned.hasOwnProperty(cKey)) delete pruned[cKey];
        } else if (f.type === "checkbox") {
          const arr = Array.isArray(pruned[f.name]) ? pruned[f.name] : [];
          const needSet = new Set(
            opts
              .filter((o) => o && o.allowComment && arr.includes(o.name))
              .map((o) => `${f.name} - ${o.name} - comment`)
          );
          opts.forEach((o) => {
            const key = `${f.name} - ${o.name} - comment`;
            if (!needSet.has(key) && pruned.hasOwnProperty(key)) delete pruned[key];
          });
        }
      });
    });

    //We dont touch review_comments_* here (alwyas keep if they exists)
    return pruned;
  }

  // -------------- UI Nodes --------------
  makeFieldNodes(field, fieldIndex) {
    const locked = this.isFormLocked();
    if(!this.modelCopy) this.modelCopy = {...this.vm.model};
    const fieldTypeMap = {
      facilities: "facilitySelect",
      users: "userSelect",
      rhUsers: "rhUsers",
      commands: "commands",
      mailList: "mailList",
      // relatedForms: "relatedForms",
      time: "time24Hrs",
      userList: "userList",
      date: "customDate",
      // folio: "folio",
      region: "region",
      timestamp: "timestamp",
      deviceDoc: "deviceDoc",
      supplier: "supplier",
      technicians: "technicians",
    }
    const mappedType = fieldTypeMap[field.type]

    if(field.type === "folio") {
      this.model[field.name] = field.options.fieldTypeCfg.data[0]?.value
      return [];
    }

    if (field.type === 'users' || field.type === 'rhUsers') {
      return this.makeUserNodes(field, mappedType, locked);
    }

    if (field.type === 'facilities') {
      return this.makeFacilityNodes(field, mappedType, locked);
    }

    // if (field.type === 'mail') {
    //   return this.makeEmailNodes(field, mappedType, locked);
    // }

    if (mappedType) {
      if (mappedType === "commands") {
        this.deferredCommands.push({ field, mappedType, locked });
        return [];
      }

      return this.makeMappedFieldNodes(field, mappedType, locked);
    }

    //FILES SECTION
    if (field.type === "files_section") {
      const fileField = {
        name: field.name,
        label: this.buildLabel(field),
        type: "component",
        component: this.customComponents.FileUploadWithPreview,
        existingFiles: this.model[field.name] || this.extraData?.files || [],
        disabled: locked,
        uploading: this.options.uploadingFieldLabel === this.buildLabel(field),
        deleting: this.options.deletingFieldLabel === this.buildLabel(field),
        onUpdateFiles: this.onUpdateFiles,
      };
      return [fileField, { component: "hr", class: "field-divider my-2" }];
    }

    //Select/Checkbox Options
    let fieldOptions;
    if (["select", "multiple_select", "checkbox"].includes(field.type)) {
      const rawOptions =
        field.options &&
        field.options.fieldTypeCfg &&
        Array.isArray(field.options.fieldTypeCfg.data)
          ? field.options.fieldTypeCfg.data
          : [];

      // Vue Formulate espera formato {label: ..., value: ...}
      fieldOptions = rawOptions.map((o, i) => {
        if (o.label && o.value) {
          return {
            label: o.label,
            value: o.value,
            allowComment: !!o.allowComment,
          };
        }
        const optionValue = o.value || o.name || `Option_${fieldIndex}_${i}`;
        const optionLabel = o.label || o.name || `Option_${fieldIndex}_${i}`;
        return {
          label: optionLabel,
          value: optionValue,
          allowComment: !!o.allowComment,
        };
      });

      const emptyOption = {
        allowComment: false,
        label: "",
        value: ""
      };

      if (!fieldOptions.length || fieldOptions[0].label !== "" || fieldOptions[0].value !== "") {
        fieldOptions.unshift(emptyOption);
      }

      if (field.type === "multiple_select") {
      }
    }

    const calcMaxlength = () => {
      if (field.options.fieldTypeCfg.data[0]?.maxLength === '1') {
        return null;
      }

      if (field.type === "text_answer" && !field.options.fieldTypeCfg.data[0]?.maxLength) {
        return 4000;
      }

      return field.options.fieldTypeCfg.data[0]?.maxLength;
    };

    const hint =  field.options.fieldTypeCfg.data[0]?.hint
    const maxLength = calcMaxlength()
    const help =  `${hint ?? ""} ${maxLength ? " - Máximo " + maxLength + " caracteres." : ""}`
    const readOnly = field.options.fieldTypeCfg.data[0]?.readOnly || false;
    const isRequired = field.options && field.options.required
    const rules = [];
    if (isRequired) {
      rules.push("required");
    }
    if (maxLength) {
      rules.push(`max:${maxLength},length`);
    }
    rules.join("|");

    let mainField = {
      id: field.item_id + this.vm._uid,
      name: field.name,
      label: this.buildLabel(field),
      type: this.convertFieldType(field.type),
      disabled: locked,
      validation: this.isValidationRequired ? rules : "",
      help,
      validationName: this.buildLabel(field),
      attrs: {}
      // options: fieldOptions,
    };


    if (maxLength) {
      mainField.maxlength = maxLength;
    }

    if (readOnly) {
      mainField.readonly = readOnly;
    }

    if (field.type === "multiple_select" || field.type === "select") {
      if (!fieldOptions) {
        fieldOptions = [];
      }
      mainField.options = fieldOptions.sort((a, b) =>
        (a?.label || '').localeCompare(b?.label || '', undefined, { sensitivity: 'base' })
      );

    } else if (field.type === "checkbox" && field.options?.fieldTypeCfg?.data.length > 1) {
      mainField.options = fieldOptions
      .filter(item => item.label?.trim() || item.value?.trim())
      .map(item => ({
        ...item,
        id: Date.now().toString(36) + Math.random().toString(36).slice(2)
      }))
    }

    if(field.type === "multiple_select") {
      mainField.multiple = true;
      mainField.attrs = mainField.attrs || {};
      mainField.attrs.multiple = true;
      if (!this.model[field.name] || !Array.isArray(this.model[field.name])) {
        this.vm.model[field.name] = [];
      }
    }

    if(field.type === "keyword") {
      mainField.type = "text";
      mainField.disabled = true;
      const regions = field.options?.fieldTypeCfg?.data &&  field.options?.fieldTypeCfg?.data.length > 5 ? field.options?.fieldTypeCfg?.data : []
      const region = regions.find(r => this.normalizeStateName(r.state) == this.normalizeStateName(this.lastRegionOfFacilitySelected))
      const keyword = region?.keyword
      mainField.value = keyword;
      this.vm.model[mainField.name] = keyword;
    }

    if(field.type === "text_answer") {
      mainField.rows = 10;
    }

    if(field.type === "placeholder") {
      mainField.readonly = true
      mainField.value = field?.options?.fieldTypeCfg?.data[0]?.value || ''
    }
    const commentsNames = [
      "Descripción del evento",
      "Descripcion del evento",
      "Descripción",
      "Comentarios"
    ];
    if(commentsNames.includes(field.name)) {
      mainField.value = this.extraData.eventComment ?? null
    }

    if(field.type === "text" && field.options?.isGroup) {
      mainField = {
        type: 'group',
        repeatable: locked ? false : true,
        name: field.name,
        addLabel: '+ Input',
        validation: this.isValidationRequired ? rules : "",
        maxlength: maxLength,
        children: [
          {
            name: field.name,
            label: this.buildLabel(field),
            type: this.convertFieldType(field.type),
            disabled: locked,
            validation: this.isValidationRequired ? rules : "",
            options: fieldOptions,
            maxlength: maxLength,
            help
          },
        ]
      }
      // this is only if exists a question with old version
      const isAnUngroupedQuestion = this.modelCopy[field.name];
      if(isAnUngroupedQuestion && typeof isAnUngroupedQuestion === "string") {
        this.vm.model[field.name] = []
        this.vm.model[field.name][0] = { [field.name]: isAnUngroupedQuestion }
      }
    }

    if(field.type === "text" && field.options.fieldTypeCfg.data[0]?.isTypeNumber) {
      mainField.type = 'number'
      mainField.min = 0
    }


    const nodes = [mainField];

    //Conditional comments by option
    const opts = Array.isArray(fieldOptions) ? fieldOptions : [];
    if (field.type === "select") {
      const sel = this.model[field.name];
      const match = opts.find((o) => o.value === sel);
      if (match && match.allowComment) {
        nodes.push({
          name: `${field.name} - comment`,
          label: `${this.buildLabel(field)} - ${this.vm.$t("risk.comment")}`,
          type: "textarea",
          disabled: locked,
        });
      }
    } else if (field.type === "multiple_select") {
      const selArr = Array.isArray(this.model[field.name]) ? this.model[field.name] : [];
      opts
        .filter((o) => o.allowComment && selArr.includes(o.value))
        .forEach((o) => {
          nodes.push({
            name: `${field.name} - ${o.value} - comment`,
            label: `${this.buildLabel(field)} - ${o.value} - ${this.vm.$t("risk.comment")}`,
            type: "textarea",
            disabled: locked,
          });
        });
    } else if (field.type === "checkbox") {
      if(field.options?.fieldTypeCfg?.data.length > 1) {
        const selArr = Array.isArray(this.model[field.name]) ? this.model[field.name] : [];
        opts
          .filter((o) => o.allowComment && selArr.includes(o.value))
          .forEach((o) => {
            nodes.push({
              name: `${field.name} - ${o.value} - comment`,
              label: `${this.buildLabel(field)} - ${o.value} - ${this.vm.$t("risk.comment")}`,
              type: "textarea",
              disabled: locked,
            });
          });
      } else {
        if(field.options?.fieldTypeCfg?.data[0]?.allowComment) {
          nodes.push({
            name: `${field.name} - comment`,
            label: `${this.buildLabel(field)} - ${this.vm.$t("risk.comment")}`,
            type: "textarea",
            disabled: locked,
          });
        }
      }

    }

    // Divider
    nodes.push({ component: "hr", class: "field-divider my-2" });
    return nodes;
  }

  makeMappedFieldNodes(field, mappedType, locked) {
    const fieldOptions = {
      ...field.options,
      lastFacilitySelected: this.lastFacilitySelected,
      lastRegionOfFacilitySelected: this.lastRegionOfFacilitySelected,
      facilityId: this.facilityId,
      questionLabel: this.buildLabel(field),
      fieldName: field.name,
      formId: this.formId,
      onCommandFinished: this.options.onCommandFinished,
    };

    const node = {
      name: field.name,
      type: mappedType,
      disabled: locked,
      fieldOptions,
      validation: this.isValidationRequired
        ? field.options && field.options.required
          ? "required"
          : ""
        : "",
    };

    if (mappedType !== "deviceDoc") node.label = this.buildLabel(field);

    return [node, { component: "hr", class: "field-divider my-2" }];
  }

  normalizeStateName(name) {
    if (!name) return ''

    return name
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
  }

  makeEmailNodes(field, mappedType, locked) {
    const buildSuffix = (base, key) => {
      return `${base} [${key}]`;
    }
    const base = field.name;
    const titleName  = buildSuffix(base, 'Título');
    const dateName  = buildSuffix(base, 'Fecha');
    const localTimeName  = buildSuffix(base, 'Hora local');
    const centralTimeName  = buildSuffix(base, 'Hora central');
    const contentName  = buildSuffix(base, 'Descripción');
    const nodes = [];

    nodes.push(
      // {
      //   name: titleName,
      //   label: "Título",
      //   type: 'text',
      //   value: field?.options?.fieldTypeCfg?.data[0]?.title || null
      // },
      {
        name: dateName,
        label: "Fecha",
        type: 'text',
        value: field?.options?.fieldTypeCfg?.data[0]?.date || null,
        //disabled: true,
      },
      {
        name: localTimeName,
        label: "Hora local",
        type: 'text',
        value: field?.options?.fieldTypeCfg?.data[0]?.localTime || null,
        //disabled: true,
      },
      {
        name: centralTimeName,
        label: "Hora central",
        type: 'text',
        value: field?.options?.fieldTypeCfg?.data[0]?.centralTime || null,
        //disabled: true,
      },
      //{
      //  name: contentName,
      //  label: "Descripción",
      //  type: 'textarea',
      //  rows: 10,
      //  value: field?.options?.fieldTypeCfg?.data[0]?.mailContent || null,
      //  disabled: true,
      //},
    );

    return nodes;
  }

  makeUserNodes(field, mappedType, locked) {
    const buildSuffix = (base, key) => {
      return `${base} [${key}]`;
    }

    const base = field.name;
    const exFieldSoeid = buildSuffix(base, 'SOEID');
    const exFieldFullName  = buildSuffix(base, 'Nombre');
    const exFieldProfile = buildSuffix(base, 'Puesto');

    const nodes = [];

    nodes.push({
      name: field.name,
      type: mappedType,
      disabled: locked,
      fieldOptions: field.options,
      validation: this.isValidationRequired ? field.options && field.options.required ? "required" : "" : "",
      extraFields: {
        soeid: exFieldSoeid,
        full_name: exFieldFullName,
        profile: exFieldProfile,
        self: field.name,
        questionLabel: this.buildLabel(field),
        userAnswer: this.extraData?.userAnswer
      }
    });

    // this is only if exists a question with old version
    const isAnUngroupedQuestion = this.modelCopy[field.name];
    if(isAnUngroupedQuestion && !Array.isArray(isAnUngroupedQuestion)) {
      this.vm.model[field.name] = []
      this.vm.model[field.name][0] = {
        [field.name]: isAnUngroupedQuestion,
        [exFieldSoeid]: this.modelCopy[exFieldSoeid],
        [exFieldFullName]: this.modelCopy[exFieldFullName],
        [exFieldProfile]: this.modelCopy[exFieldProfile],
      }
    }

    return nodes;
  }

  makeFacilityNodes(field, mappedType, locked) {
    const buildSuffix = (base, key) => {
      return `${base} [${key}]`;
    }

    const base = field.name;
    const exFieldCategories = buildSuffix(base, 'Categorías');
    const exFieldRegion = buildSuffix(base, 'Estado');
    const exFieldSubregion = buildSuffix(base, 'Subregión');
    const exFieldSirh = buildSuffix(base, 'SIRH');
    const exFieldMunicipality = buildSuffix(base, 'Municipio');
    const exFieldRems = buildSuffix(base, 'REMS');
    const exFieldIdSeproban = buildSuffix(base, 'ID SEPROBAN');
    const exFieldLat = buildSuffix(base, 'Lat');
    const exFieldILng = buildSuffix(base, 'Lng');
    const exFieldId = buildSuffix(base, 'id');
    const exFieldName = buildSuffix(base, 'name');
    const nodes = [];

    nodes.push({
      id: field.item_id,
      name: field.name,
      // label: this.buildLabel(field),
      type: mappedType,
      disabled: locked,
      validation: this.isValidationRequired ? field.options && field.options.required ? "required" : "" : "",
      fieldOptions: field.options,
      extraFields: {
        self: field.name,
        categories: exFieldCategories,
        region: exFieldRegion,
        subregion: exFieldSubregion,
        questionLabel: this.buildLabel(field),
        sirh: exFieldSirh,
        municipality: exFieldMunicipality,
        facilityAnswer: this.extraData?.facilityAnswer,
        rems: exFieldRems,
        id_seproban: exFieldIdSeproban,
        lat: exFieldLat,
        lng: exFieldILng,
        id: exFieldId,
        name: exFieldName,
      },
      extraData: this.extraData,
      updateRegionFromFacility: (region) => {
        this.lastRegionOfFacilitySelected = region
      },
      updateFacility: (facility) => {
        this.lastFacilitySelected = facility
        this.facilityId = facility?.facility?.value ?? null
      },
    })
    // this is only if exists a question with old version
    const isAnUngroupedQuestion = this.modelCopy[field.name];
    if(isAnUngroupedQuestion && !Array.isArray(isAnUngroupedQuestion)) {
      this.vm.model[field.name] = []
      this.vm.model[field.name][0] = {
        [field.name]:isAnUngroupedQuestion,
        [exFieldCategories]: this.modelCopy[exFieldCategories],
        [exFieldRegion]: this.modelCopy[exFieldRegion],
        [exFieldSubregion]: this.modelCopy[exFieldSubregion],
      }
    }

    return nodes;
  }

  buildLabel(field) {
    return field.name.replace(/\|.*/, '');
  }

  buildDefaultFields() {
    // Default fields
    const user = store.getters["user/getUser"];
    const nodes = [];

    nodes.push({
      id: 'form_owner',
      name: 'SOEID Analista',
      type: 'hidden',
      value: `${user.user}|${user.name} ${user.first_name} ${user.second_name}`,
    });

    return nodes;
  }

  // -------------- Complete Render with subsections --------------
  convert() {
    const schema = [];
    this.deferredCommands = [];

    const processed = this.getProcessedFields().filter((f) => {
      if (!this.options.showAlarmsOnly) return true;
      return !!(f && f.options && f.options.alarms === true);
    });

    //For visibility ( relative order)
    const { order, groups } = this.groupBySection(processed);

    //To setup sections/subsections
    const { structured } = this.groupBySectionSubsection(processed);

    order.forEach((secName) => {
      const secFields = (groups.get(secName) || {}).fields || [];
      const indexMap = new Map(secFields.map((f, idx) => [f.item_id, idx]));

      const entry = structured.get(secName) || { root: [], sub: new Map(), subOrder: [] };

      const sectionChildren = [];
      let sectionVisibleCount = 0;

      // a) Fields with a subsection
      const rootNodes = [];
      let rootVisible = 0;
      (entry.root || []).forEach((f) => {
        const idx = indexMap.get(f.item_id) ?? 0;
        if (!this.isFieldVisible(f, secFields, idx, this.model) && !this.isFirstLoading) return;
        rootNodes.push(...this.makeFieldNodes(f, idx));
        rootVisible += 1;
      });
      if (rootVisible) {
        sectionChildren.push(...rootNodes);
        sectionVisibleCount += rootVisible;
      }

      // b) Subsections
      const subOrder = entry.subOrder || [];
      subOrder.forEach((subName) => {
        const subNodes = [];
        let subVisible = 0;

        const items = entry.sub.get(subName) || [];
        items.forEach((f) => {
          const idx = indexMap.get(f.item_id) ?? 0;
          if (!this.isFieldVisible(f, secFields, idx, this.model)) return;
          subNodes.push(...this.makeFieldNodes(f, idx));
          subVisible += 1;
        });
        if (!subVisible) return;


        //Wrapp subsection with SectionGroup (level 2) and delete hr final
        const cleaned =
          subNodes.length && subNodes[subNodes.length - 1].component === "hr"
            ? subNodes.slice(0, -1)
            : subNodes;

        sectionChildren.push({
          component: this.customComponents.SectionGroup || "div",
          title: subName,
          count: subVisible,
          initiallyCollapsed: false,
          level: 2,
          children: cleaned,
        });
        sectionVisibleCount += subVisible;
      });

      if (!sectionVisibleCount) return;

      //Deletes hr if the section is the lastone
      if (
        sectionChildren.length &&
        sectionChildren[sectionChildren.length - 1].component === "hr"
      ) {
        sectionChildren.pop();
      }
      const questions = sectionChildren.length === 1 ? sectionChildren[0].children : sectionChildren
      //Group Section (level 1)
      schema.push({
        component: "div",
        children: questions,
      });
    });

    if (this.deferredCommands.length) {
      const commandNodes = [];

      this.deferredCommands.forEach(({ field, mappedType, locked }) => {
        commandNodes.push(...this.makeMappedFieldNodes(field, mappedType, locked));
      });

      if (
        commandNodes.length &&
        commandNodes[commandNodes.length - 1].component === "hr"
      ) {
        commandNodes.pop();
      }

      if (commandNodes.length) {
        schema.push({
          component: "div",
          children: commandNodes,
        });
      }

      this.deferredCommands = [];
    }

    schema.push(...this.buildDefaultFields());

    let shouldShowSave = !this.isFinalState();
    if (shouldShowSave) {
      const buttonGroup = this.buildGroup();
      const reloadButton = this.options.reloadButton || {};
      buttonGroup.children.push(
        this.buildButton({
          id: "save-op-sec-form",
          name: "submit",
          type: "button",
          text: this.vm.$t?.("actions.save") || "Save",
          classes: ["btn", "btn-primary", "text-uppercase", "mr-1"],
          disabled: false,
        })
      );

      if (reloadButton.visible) {
        buttonGroup.children.push(
          this.buildButton({
            id: "reload-op-sec-form",
            name: "reload",
            type: "button",
            text:
              this.vm.$t?.("operational_security.evaluation.reload") ||
              "Reload",
            classes: ["btn", "btn-outline-primary", "text-uppercase"],
            disabled: !!reloadButton.disabled,
          })
        );
      }
      // buttonGroup.children.push(
      //   this.buildButton({
      //     name: "Finish",
      //     type: "submit",
      //     text: this.vm.$t?.("action_plan.nodes.terminate.name") || "Finish",
      //     classes: ["btn", "btn-success", "text-uppercase", "mr-1"],
      //     disabled: false,
      //   })
      // );
      schema.push(buttonGroup);
    }
    this.isFirstLoading = false;
    return schema;
  }
}
